const DEFAULT_LOCALE = "en-gb";
const DEFAULT_CURRENCY = "GBP";

const roundAmount = (amount: string | number, decimalPlaces: number) =>
  Math.round(Number(amount) * Math.pow(10, decimalPlaces)) /
  Math.pow(10, decimalPlaces);

// get the number of decimal points from an amount
// Currency values returned by the server have been formatted to the desired number of decimal points
// We want to maintain their number format after formatting in the front end
const getDp = (amount: string | number) =>
  (String(amount).split(".")[1] || "").length;

type FormatOptions = {
  decimalPlaces?: number; // specify a decimal places
  transform?: (amount: number) => number; // specify a transformation function to transform the input value before output
};

const formatMoney = (
  currency: string | undefined,
  amount: string | undefined,
  getPrefix: (amount: number) => string,
  getSuffix: (amount: number) => string,
  options?: FormatOptions,
) => {
  if (amount === "" || amount === undefined) {
    return "";
  }
  const { decimalPlaces, transform } = options || {};
  const dp = decimalPlaces ?? getDp(amount);
  const transformedAmount = transform
    ? transform(Number(amount))
    : Number(amount);
  const prefix = getPrefix(transformedAmount);
  const suffix = getSuffix(transformedAmount);
  const absoluteAmount = roundAmount(Math.abs(transformedAmount), dp).toFixed(
    dp,
  );
  try {
    const localizedAmount = Number(absoluteAmount).toLocaleString(
      navigator.language || DEFAULT_LOCALE,
      {
        style: "currency",
        currency: currency || DEFAULT_CURRENCY,
        minimumFractionDigits: dp,
        maximumFractionDigits: dp,
      },
    );
    return `${prefix}${localizedAmount}${suffix}`;
  } catch (e) {
    return `${prefix}${absoluteAmount}${suffix}`;
  }
};

// Formats an amount with the currency symbol in the local format
// Prepend "-" for negative amounts
export const formatMoneyWithSign = (
  currency: string | undefined,
  amount: string | undefined,
  options?: FormatOptions,
) => {
  const getPrefix = (amt: string | number) => (Number(amt) < 0 ? "-" : "");
  const getSuffix = () => "";
  return formatMoney(currency, amount, getPrefix, getSuffix, options);
};

// Formats an amount with the currency symbol in the local money format
// Append CR for negative amounts
// Set showDR to append DR for positive amounts
export const formatMoneyWithCR = (
  currency: string | undefined,
  amount: string | undefined,
  options?: FormatOptions & { showDR?: boolean },
) => {
  const { showDR, ...rest } = options || {};
  const getPrefix = () => "";
  const getSuffix = (amt: string | number) =>
    Number(amt) < 0 ? " CR" : showDR ? " DR" : "";
  return formatMoney(currency, amount, getPrefix, getSuffix, { ...rest });
};
